import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { styled } from "@mui/material/styles";

// --- CONSISTENCY CONSTANTS ---
const TEXT_GREY = "#444746";   // For labels/descriptions
const TEXT_BLACK = "#000000";  // For values
const BORDER_COLOR = "#d1e3fa"; // Light blue border
const HEADER_BG = "#e3f2fd";    // Light blue header background

const StyledTableContainer = styled(TableContainer)(({ maxHeight }) => ({
  maxHeight: maxHeight,
  overflowY: "auto",
  overflowX: "hidden",
  border: `1px solid ${BORDER_COLOR}`,
  borderRadius: "12px", // Matching the rounded corners of the widget
  boxShadow: "none",
}));

const StyledTable = styled(Table)(() => ({
  tableLayout: "fixed",
  width: "100%",
  "& th": {
    backgroundColor: HEADER_BG,
    color: TEXT_BLACK,
    fontWeight: 600,
    fontSize: "1rem",
    textAlign: "left", // Tables usually read better left-aligned
    padding: "16px",
    borderBottom: `1px solid ${BORDER_COLOR}`,
    position: "sticky",
    top: 0,
    zIndex: 2,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
  },
  "& td": {
    borderColor: BORDER_COLOR,
    padding: "12px 16px",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
}));

const AccordionTableWrapper = styled("div")(() => ({
  backgroundColor: "#f8fafd", // Very light tint for nested tables
  padding: "15px",
  borderBottom: `1px solid ${BORDER_COLOR}`,
}));

const CustomTable = ({ columns, rows, accordion = false, accordionTableColumns, maxHeight }) => {
  const [openRow, setOpenRow] = useState({});

  const toggleRow = (rowIndex) => {
    setOpenRow((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  return (
    <StyledTableContainer component={Paper} maxHeight={maxHeight}>
      <StyledTable aria-label="dynamic table" stickyHeader>
        <TableHead>
          <TableRow>
            {accordion && <TableCell sx={{ width: 50 }} />}
            {columns.map((column) => (
              <TableCell key={column.label}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <React.Fragment key={rowIndex}>
              <TableRow sx={{ "&:last-child td": { border: 0 } }}>
                {accordion && (
                  <TableCell>
                    <IconButton onClick={() => toggleRow(rowIndex)} size="small">
                      {openRow[rowIndex] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                )}
                {columns.map((column, colIndex) => (
                  <TableCell key={column.field}>
                    <Typography
                      sx={{
                        // First column stays grey (label), others black (value)
                        color: colIndex === 0 ? TEXT_GREY : TEXT_BLACK,
                        fontWeight: colIndex === 0 ? 400 : 500,
                        fontSize: "0.95rem",
                        whiteSpace: "pre-line", // Respects line breaks for addresses
                      }}
                    >
                      {row[column.field]}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
              
              {/* Accordion Content */}
              {accordion && openRow[rowIndex] && row.history && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} sx={{ p: 0, bgcolor: "#f8fafd" }}>
                    <AccordionTableWrapper>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {accordionTableColumns.map((col) => (
                              <TableCell key={col.label} sx={{ color: TEXT_GREY, fontWeight: 600 }}>
                                {col.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {row.history.map((historyRow, index) => (
                            <TableRow key={index}>
                              {accordionTableColumns.map((col) => (
                                <TableCell key={col.field} sx={{ color: TEXT_BLACK }}>
                                  {historyRow[col.field]}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionTableWrapper>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </StyledTable>
    </StyledTableContainer>
  );
};

export default CustomTable;